import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import App from "./App.tsx";
import "./index.css";

// ── Sentry (error tracking) ────────────────────────────────────────
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// PII scrubbing: hash emails, redact tokens/keys, drop sensitive headers/cookies.
const EMAIL_RE = /([a-zA-Z0-9._%+-]+)@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
const TOKEN_RE = /(eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)/g; // JWTs
const SECRET_HEADERS = new Set([
  "authorization", "cookie", "set-cookie", "x-api-key", "apikey",
  "x-supabase-auth", "x-access-token",
]);

function scrubString(input: string): string {
  return input
    .replace(EMAIL_RE, (_, _u, d) => `[email:${d}]`)
    .replace(TOKEN_RE, "[redacted-jwt]");
}

function scrubObject(obj: unknown, depth = 0): unknown {
  if (depth > 5 || obj == null) return obj;
  if (typeof obj === "string") return scrubString(obj);
  if (Array.isArray(obj)) return obj.map((v) => scrubObject(v, depth + 1));
  if (typeof obj === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (SECRET_HEADERS.has(k.toLowerCase())) { out[k] = "[redacted]"; continue; }
      out[k] = scrubObject(v, depth + 1);
    }
    return out;
  }
  return obj;
}

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Mask text/media in replays to avoid leaking PII visible on screen.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_COMMIT_SHA || undefined,
    sendDefaultPii: false,
    ignoreErrors: [
      /^ResizeObserver loop/,
      /^Non-Error exception captured/,
      /extensions/i,
      /chrome-extension/,
      /moz-extension/,
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
    beforeSend(event) {
      try {
        if (event.request?.headers) event.request.headers = scrubObject(event.request.headers) as Record<string, string>;
        if (event.request?.cookies) event.request.cookies = "[redacted]" as unknown as Record<string, string>;
        if (event.request?.data) event.request.data = scrubObject(event.request.data);
        if (event.extra) event.extra = scrubObject(event.extra) as Record<string, unknown>;
        if (event.contexts) event.contexts = scrubObject(event.contexts) as typeof event.contexts;
        if (event.message) event.message = scrubString(event.message);
        if (event.user) {
          // Keep stable hashed id only — drop email/username/ip.
          event.user = { id: event.user.id };
        }
      } catch { /* never block delivery */ }
      return event;
    },
    beforeBreadcrumb(crumb) {
      if (crumb.message) crumb.message = scrubString(crumb.message);
      if (crumb.data) crumb.data = scrubObject(crumb.data) as Record<string, unknown>;
      return crumb;
    },
  });
}

async function hashUserId(raw: string): Promise<string> {
  try {
    const buf = new TextEncoder().encode(raw);
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 32);
  } catch {
    return "anon";
  }
}

// ── PostHog (product analytics) ─────────────────────────────────────
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";
if (POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: false,
  });
}

// ── Export tracking helpers (legacy — prefer src/lib/analytics.ts) ──
export function trackEvent(event: string, properties?: Record<string, any>) {
  if (POSTHOG_KEY) {
    posthog.capture(event, properties);
  }
}

export function identifyUser(userId: string, traits?: Record<string, any>) {
  if (POSTHOG_KEY) {
    posthog.identify(userId, traits);
  }
  if (SENTRY_DSN) {
    // Hash user id for Sentry; never send email/name/ip to error tracking.
    void hashUserId(userId).then((hashed) => Sentry.setUser({ id: hashed }));
  }
}

createRoot(document.getElementById("root")!).render(<App />);
